import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { fmtUAE, TZ_LABEL } from "@/lib/dates";
import { Bell, CheckCheck } from "lucide-react";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.post("/notifications/mark-read").catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10 flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-lavender mb-3">The Wire</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
              Your <span className="text-purple">Results</span>
            </h1>
            <p className="text-white/60 mt-3 text-sm">Every match wrap-up & bonus reveal. Read top-down.</p>
          </div>
          <CheckCheck className="text-lavender hidden md:block" />
        </motion.div>

        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-surface border border-white/5 mb-6">
              <Bell className="text-white/30" size={32}/>
            </div>
            <div className="font-display text-3xl uppercase tracking-tight">All Quiet</div>
            <p className="text-white/40 text-sm mt-3">When admin posts a result, you&apos;ll find your win/miss here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((n) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                data-testid={`notif-${n.id}`}
                className={`row-hover flex items-start gap-4 p-4 rounded-xl bg-surface border ${
                  n.points > 0 ? "border-lavender/40" : "border-white/5"
                }`}
              >
                <div className={`w-12 h-12 rounded-full grid place-items-center font-mono font-bold shrink-0 ${
                  n.points >= 3 ? "bg-lavender/20 text-lavender border border-lavender/40"
                  : n.points >= 1 ? "bg-purple/15 text-purple border border-purple/40"
                  : "bg-white/5 text-white/40 border border-white/10"
                }`}>
                  {n.points > 0 ? `+${n.points}` : "0"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-display text-lg uppercase tracking-wider truncate">{n.title}</div>
                    <div className="text-[10px] tracking-widest uppercase text-white/40 whitespace-nowrap">
                      {fmtUAE(n.created_at)} {TZ_LABEL}
                    </div>
                  </div>
                  <div className="text-sm text-white/70 mt-1">{n.body}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
